import { alpha, Box, Grid, Tooltip, Typography, useTheme } from '@mui/material';

import { resolveFeeDescription } from '@/utils/fees/feeDescriptions';
import { formatPrice } from '@/utils/helper';
import { ProductEsitmation } from '@/types/ProductType';

type ProductDetailCashToCloseProps = {
  estimation: ProductEsitmation;
};

export function productDetailCashToCloseHasContent(
  estimation: ProductEsitmation | null | undefined
): boolean {
  if (!estimation) return false;
  return (
    estimation.cashToClose != null && Number.isFinite(estimation.cashToClose)
  );
}

function CashToCloseRow({ label, value }: { label: string; value: number }) {
  const theme = useTheme();
  const tip = resolveFeeDescription({ label });

  return (
    <Grid item xs={12} sm={6}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.greyShades.border1}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Tooltip title={tip} placement="top" arrow enterTouchDelay={0}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              cursor: 'help',
              borderBottom: `1px dotted ${alpha(
                theme.palette.text.secondary,
                0.45
              )}`,
              display: 'inline-block',
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        <Typography variant="subtitle1" fontWeight={700}>
          {formatPrice(value, 0)}
        </Typography>
      </Box>
    </Grid>
  );
}

export const ProductDetailCashToClose = ({
  estimation,
}: ProductDetailCashToCloseProps) => {
  const theme = useTheme();

  if (!productDetailCashToCloseHasContent(estimation)) return null;

  const rows: { label: string; value: number | undefined }[] = [
    { label: 'Down payment', value: estimation.downPayment },
    {
      label: 'Closing costs',
      value: (estimation as { closingCosts?: number }).closingCosts,
    },
    { label: 'Cash to close', value: estimation.cashToClose },
  ];

  const unbeatableDeal = (estimation as { unbeatableDeal?: number })
    .unbeatableDeal;
  if (unbeatableDeal != null && Number.isFinite(unbeatableDeal)) {
    rows.push({
      label: 'Unbeatable deal (after credit)',
      value: unbeatableDeal,
    });
  }

  return (
    <Box>
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ mb: 2, color: theme.palette.text.primary }}
      >
        Cash to close
      </Typography>
      <Grid container spacing={1.5}>
        {rows.map((row) =>
          row.value == null || !Number.isFinite(row.value) ? null : (
            <CashToCloseRow
              key={row.label}
              label={row.label}
              value={row.value}
            />
          )
        )}
      </Grid>
    </Box>
  );
};
